/**
 * Phase 5.1: ContextBuilder
 *
 * This service builds fresh context from captured component snapshots
 * for sending to the backend AI. It ensures the context contains
 * accurate, up-to-date dimension values.
 *
 * Key features:
 * - Builds context from ComponentSnapshot (captured state)
 * - Includes explicit currentDimensions with normalized pixel values
 * - Provides pre-calculated values for relative commands
 * - Detects and reports dimension units
 *
 * @see Documents/AI_Template_System/Roadmaps/AI_Chat_Enhancement_Roadmap_Phase2.md
 */

import { Component, ResponsivePosition, Position } from '@/types';
import { CSSProperties } from 'react';
import { ComponentSnapshot, commandStateManager } from './CommandStateManager';
import { EnhancedComponentContext } from './AIContextService';
import { buildSafeAiComponentContext } from './AIComponentContextSanitizer';
import type { AiBindingContextSummary } from './BindingContextSummary';

/**
 * Context dimensions with resolved values and unit information
 */
export interface ContextDimensions {
  // Resolved pixel values for calculations
  width: number;
  height: number;
  // Unit types
  widthUnit: 'px' | '%' | 'auto' | 'clamp';
  heightUnit: 'px' | '%' | 'auto' | 'clamp';
  // Raw values as stored in component
  rawWidth: string | number;
  rawHeight: string | number;
  // Pre-calculated values for relative commands
  preCalculated: {
    taller: number;
    shorter: number;
    wider: number;
    narrower: number;
    biggerWidth: number;
    biggerHeight: number;
    smallerWidth: number;
    smallerHeight: number;
  };
}

/**
 * Enhanced context for AI property modification
 */
export interface AIPropertyContext {
  component: {
    id: string;
    type: string;
    name: string;
    properties: Record<string, unknown>;
    bindingContext?: AiBindingContextSummary;
    style: CSSProperties;
    size: { width: number | string; height: number | string };
    position: Position;
    responsivePosition: ResponsivePosition;
  };
  // Viewport information
  viewport?: {
    currentBreakpoint: string;
    width: number;
    height: number;
  };
  // CRITICAL: Explicit dimension values for accurate AI processing
  currentDimensions: ContextDimensions;
  // Enhanced context from AIContextService (if available)
  enhancedContext?: EnhancedComponentContext;
  // Specialization scope for service provider AI filtering
  tradeScope?: string | null;
  // Timestamp for debugging
  contextTimestamp: number;
}

/**
 * Options for building context
 */
export interface ContextBuilderOptions {
  includeEnhancedContext?: boolean;
  viewportWidth?: number;
  viewportHeight?: number;
  currentBreakpoint?: string;
  enhancedContext?: EnhancedComponentContext;
  tradeScope?: string | null;
}

function parseSnapshotPosition(value: unknown): Position {
  if (!isRecord(value)) {
    return { x: 0, y: 0, z: 0 };
  }

  return {
    x: typeof value.x === "number" ? value.x : 0,
    y: typeof value.y === "number" ? value.y : 0,
    z: typeof value.z === "number" ? value.z : 0,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function getRequestContextRecord(value: unknown): Record<string, unknown> {
  return isRecord(value) ? value : {};
}

/**
 * Builds fresh context from a component snapshot for the AI backend.
 *
 * This function is the key to Phase 5.1 - it ensures that the context
 * sent to the backend reflects the actual current state of the component,
 * not a stale prop value.
 */
export function buildContextFromSnapshot(
  snapshot: ComponentSnapshot,
  options: ContextBuilderOptions = {}
): AIPropertyContext {
  const {
    viewportWidth = 1200,
    viewportHeight = 800,
    currentBreakpoint = 'desktop',
    enhancedContext,
  } = options;

  const componentType = String(snapshot.properties._type || 'unknown');
  const componentName = String(snapshot.properties._name || snapshot.properties.name || 'Unnamed');
  const safeComponentContext = buildSafeAiComponentContext({
    id: snapshot.id,
    type: componentType,
    name: componentName,
    properties: snapshot.properties,
  });

  // Calculate pre-computed values for relative commands
  const preCalculated = commandStateManager.calculateRelativeValues(snapshot.id) || {
    taller: Math.round(snapshot.resolvedHeight * 1.5),
    shorter: Math.round(snapshot.resolvedHeight * 0.75),
    wider: Math.round(snapshot.resolvedWidth * 1.5),
    narrower: Math.round(snapshot.resolvedWidth * 0.75),
    biggerWidth: Math.round(snapshot.resolvedWidth * 1.5),
    biggerHeight: Math.round(snapshot.resolvedHeight * 1.5),
    smallerWidth: Math.round(snapshot.resolvedWidth * 0.75),
    smallerHeight: Math.round(snapshot.resolvedHeight * 0.75),
  };

  const currentDimensions: ContextDimensions = {
    width: snapshot.resolvedWidth,
    height: snapshot.resolvedHeight,
    widthUnit: snapshot.widthUnit,
    heightUnit: snapshot.heightUnit,
    rawWidth: snapshot.size.width,
    rawHeight: snapshot.size.height,
    preCalculated,
  };

  const context: AIPropertyContext = {
    component: {
      id: snapshot.id,
      type: componentType,
      name: componentName,
      properties: safeComponentContext.properties,
      bindingContext: safeComponentContext.bindingContext,
      style: snapshot.style,
      size: {
        width: snapshot.size.width,
        height: snapshot.size.height,
      },
      position: parseSnapshotPosition(snapshot.properties._position),
      responsivePosition: snapshot.responsivePosition,
    },
    viewport: {
      currentBreakpoint,
      width: viewportWidth,
      height: viewportHeight,
    },
    currentDimensions,
    tradeScope: options.tradeScope || null,
    contextTimestamp: snapshot.timestamp,
  };

  if (enhancedContext) {
    context.enhancedContext = enhancedContext;
  }

  return context;
}

/**
 * Builds context directly from a component (convenience function)
 * This captures state and builds context in one step.
 */
export function buildContextFromComponent(
  component: Component,
  options: ContextBuilderOptions = {}
): AIPropertyContext {
  // Capture fresh state
  const snapshot = commandStateManager.captureState(component);

  // Build context from snapshot
  return buildContextFromSnapshot(snapshot, {
    ...options,
    // Override properties with component metadata
  });
}

/**
 * Formats the dimension context for inclusion in the backend prompt.
 * This creates a string that can be appended to the AI prompt.
 */
export function formatDimensionContextForPrompt(dimensions: ContextDimensions): string {
  const lines: string[] = [
    '',
    'CRITICAL - Current component dimensions (use these exact values as baseline):',
    `- Current width: ${dimensions.width}px (stored as: ${dimensions.rawWidth})`,
    `- Current height: ${dimensions.height}px (stored as: ${dimensions.rawHeight})`,
    '',
    'Pre-calculated values for relative commands:',
    `- "taller" = increase height by 50% = ${dimensions.preCalculated.taller}px`,
    `- "shorter" = decrease height by 25% = ${dimensions.preCalculated.shorter}px`,
    `- "wider" = increase width by 50% = ${dimensions.preCalculated.wider}px`,
    `- "narrower" = decrease width by 25% = ${dimensions.preCalculated.narrower}px`,
    `- "bigger" = increase BOTH dimensions by 50% = ${dimensions.preCalculated.biggerWidth}px x ${dimensions.preCalculated.biggerHeight}px`,
    `- "smaller" = decrease BOTH dimensions by 25% = ${dimensions.preCalculated.smallerWidth}px x ${dimensions.preCalculated.smallerHeight}px`,
    '',
    'IMPORTANT RULES:',
    '- NEVER return "auto" for dimensions. Always return calculated pixel values.',
    '- For "taller"/"shorter" commands: ONLY modify height, preserve width.',
    '- For "wider"/"narrower" commands: ONLY modify width, preserve height.',
    '- For "bigger"/"smaller" commands: Modify BOTH dimensions.',
    '- Use the pre-calculated values above - do not recalculate.',
    '',
  ];

  return lines.join('\n');
}

/**
 * Creates a minimal dimension context for quick commands
 * (smaller token usage for simple operations)
 */
export function createMinimalDimensionContext(dimensions: ContextDimensions): string {
  return `Current: ${dimensions.width}x${dimensions.height}px. For size changes: taller=${dimensions.preCalculated.taller}px, shorter=${dimensions.preCalculated.shorter}px, wider=${dimensions.preCalculated.wider}px, narrower=${dimensions.preCalculated.narrower}px.`;
}

/**
 * Validates that the context has valid dimension values
 */
export function validateDimensionContext(context: AIPropertyContext): {
  isValid: boolean;
  warnings: string[];
} {
  const warnings: string[] = [];

  const dims = context.currentDimensions;

  if (dims.width <= 0) {
    warnings.push('Width is 0 or negative - relative commands may fail');
  }

  if (dims.height <= 0) {
    warnings.push('Height is 0 or negative - relative commands may fail');
  }

  if (dims.widthUnit === 'auto' && dims.width === 200) {
    warnings.push('Width is auto (using fallback 200px) - results may be unexpected');
  }

  if (dims.heightUnit === 'auto' && dims.height === 100) {
    warnings.push('Height is auto (using fallback 100px) - results may be unexpected');
  }

  return {
    isValid: warnings.length === 0,
    warnings,
  };
}

/**
 * Merges the dimension context into the request body for the backend
 */
export function mergeContextIntoRequestBody(
  requestBody: Record<string, unknown>,
  context: AIPropertyContext
): Record<string, unknown> {
  return {
    ...requestBody,
    context: {
      ...getRequestContextRecord(requestBody.context),
      currentDimensions: context.currentDimensions,
      contextTimestamp: context.contextTimestamp,
      ...(context.tradeScope ? { tradeScope: context.tradeScope } : {}),
    },
  };
}

// Export a utility object for convenience
export const contextBuilder = {
  buildFromSnapshot: buildContextFromSnapshot,
  buildFromComponent: buildContextFromComponent,
  formatForPrompt: formatDimensionContextForPrompt,
  createMinimal: createMinimalDimensionContext,
  validate: validateDimensionContext,
  mergeIntoRequest: mergeContextIntoRequestBody,
};
